import React from "react";
import { STEP_CONFIG } from "./constants";

/**
 * Renders rejection/input-required message for document or evaluation status.
 * Used by upload_documents_card, kyc_card, credit_evaluation.
 */
export const renderRejectionMessage = (status, message) =>
  message &&
  message !== "None" &&
  message !== "" &&
  ["input_required", "rejected"].includes(status) && (
    <div className="dash_alertDiv">
      <p>{message}</p>
    </div>
  );

const SIGNED_AGREEMENT_STATUSES = ["signed", "verified"];

export const areRequiredAgreementsSigned = (agreements = []) => {
  const hasSignedPersonal = agreements.some(
    (a) =>
      a.agreement_type === "personal_guarantee" &&
      SIGNED_AGREEMENT_STATUSES.includes(a.status)
  );
  const hasSignedForfaiting = agreements.some(
    (a) =>
      a.agreement_type === "forfaiting_agreement" &&
      SIGNED_AGREEMENT_STATUSES.includes(a.status)
  );
  return hasSignedPersonal && hasSignedForfaiting;
};

export const computeRequiredAgreementsStatus = (agreements = []) => {
  const hasSignedPersonal = agreements.some(
    (a) =>
      a.agreement_type === "personal_guarantee" &&
      SIGNED_AGREEMENT_STATUSES.includes(a.status)
  );
  const hasSignedForfaiting = agreements.some(
    (a) =>
      a.agreement_type === "forfaiting_agreement" &&
      SIGNED_AGREEMENT_STATUSES.includes(a.status)
  );

  if (hasSignedPersonal && hasSignedForfaiting) {
    return "signed";
  }
  if (hasSignedPersonal || hasSignedForfaiting) {
    return "partial";
  }
  return "pending";
};

/**
 * Derives step completion flags from application status.
 */
export const calculateStepStatus = (applicationStatus, agreementsDone) => {
  const loanStatus = applicationStatus?.loan?.status || "incomplete";
  const { status: kycCurrentStatus } = applicationStatus?.kyc?.current_status || {};

  return {
    loanStepComplete: ['completed', 'verified'].includes(loanStatus),
    kycStepComplete: ['completed'].includes(kycCurrentStatus),
    kycStepVerified: ['verified'].includes(kycCurrentStatus),
    connectSellerComplete: Boolean(applicationStatus?.integrations?.length),
    applicationStepComplete: loanStatus === 'verified' && kycCurrentStatus === 'verified',
    agreementStepComplete:
      applicationStatus?.evaluation?.status === 'approved' &&
      applicationStatus?.settlement_bank_account?.status === 'verified' && agreementsDone,
    enableInvoiceDashboard:
      loanStatus === 'verified' &&
      kycCurrentStatus === 'verified' &&
      applicationStatus?.evaluation?.status === 'approved' &&
      applicationStatus?.settlement_bank_account?.status === 'verified' &&
      applicationStatus?.virtual_account?.status === 'approved',
  };
};

/**
 * Returns the step index the user should start on based on current status.
 */
export function getInitialActiveStep(applicationStatus, stepStatus) {
  const { agreementStepComplete, applicationStepComplete } = stepStatus;

  if (agreementStepComplete) {
    return STEP_CONFIG.ACCOUNT_SETUP;
  }
  if (applicationStepComplete) {
    return STEP_CONFIG.OFFER_AGREEMENT;
  }
  return STEP_CONFIG.APPLICATION;
}

/**
 * Tooltip text for the Application step "Next" button based on completion state.
 */
export function getApplicationStepTooltipKey(stepStatus) {
  if (!stepStatus.kycStepComplete && !stepStatus.loanStepComplete) {
    return "upload_documents_required";
  }
  if (!(stepStatus.kycStepComplete || stepStatus.kycStepVerified)) {
    return "kyc_required";
  }
  if (stepStatus.loanStepComplete && stepStatus.kycStepComplete) {
    return "document_verification_in_progress";
  }
  return "";
}

export function getApplicationStepTooltip(stepStatus) {
  const key = getApplicationStepTooltipKey(stepStatus);
  const tooltipByKey = {
    upload_documents_required: "Please upload documents to continue",
    kyc_required: "Please upload the business kyc and shareholders kyc to continue",
    document_verification_in_progress:
      "Document verification in progress (1–2 business days). Next step will be enabled soon.",
  };
  return tooltipByKey[key] || "";
}
